/*
 * <carni-express-check> : "Am I in the 90 minute Express zone?"
 *
 * WHAT IT DOES
 * Takes a point, either from the browser's geolocation or from a pin the customer drags, and
 * tests it against the real delivery contour in assets/carni-express-zone.json. That file is a
 * 29.9 km toll-free DRIVING distance contour from the store (25.142225, 55.226750), clipped to
 * Dubai city limits, so it is not a circle and it does not follow neighbourhood names. The old
 * modal listed 16 area names, which is wrong at the edges: the contour cuts straight through
 * Mirdif, for instance, so west Mirdif is inside and Mirdif City Centre is not.
 *
 * WHY A CONTOUR AND NOT A RADIUS
 * A radius would claim Al Maktoum airport (24 km straight line) is closer than Deira (16 km),
 * when by road it is the other way round. Driving distance is what the courier actually spends.
 *
 * GEOMETRY
 * Rings are stored as Google encoded polylines at 1e5 precision, which is about 1.1 m on the
 * ground and round-trips the source KML to within 0.75 m. 5181 points cost 18 KB raw and about
 * 7 KB over the wire, so the full contour ships unsimplified and the test is exact rather than
 * an approximation of an approximation. There are 12 rings: one large one carrying 5 holes, and
 * 11 slivers left by the city-limits clip that total 0.03 km2. The slivers cost 60 points, so
 * they stay in rather than being tidied away and quietly changing someone's answer.
 *
 * WHY IT NEVER SAYS A HARD YES NEAR THE EDGE
 * Two different errors stack up at the boundary: the browser's own position accuracy (which it
 * reports, and which is often 20 m on GPS but several hundred metres on wifi or IP) and the
 * contour's own tolerance. Inside the larger of those two, this reports "on the edge" and sends
 * the customer to checkout to confirm rather than promising a 90 minute slot the driver cannot
 * make. A wrong yes here is a refunded order and a bad first delivery, which is exactly the
 * moment we are trying to protect.
 *
 * Loads Leaflet and the zone file only when the customer actually asks, so the pages that merely
 * render the banner pay nothing for it.
 */
(function () {
  if (customElements.get('carni-express-check')) return;

  var EDGE_FLOOR_M = 150; // contour tolerance: never claim certainty inside this band

  function decodePolyline(str) {
    var pts = [], i = 0, lat = 0, lon = 0, b, shift, result;
    while (i < str.length) {
      shift = 0; result = 0;
      do { b = str.charCodeAt(i++) - 63; result |= (b & 31) << shift; shift += 5; } while (b >= 32);
      lat += (result & 1) ? ~(result >> 1) : (result >> 1);
      shift = 0; result = 0;
      do { b = str.charCodeAt(i++) - 63; result |= (b & 31) << shift; shift += 5; } while (b >= 32);
      lon += (result & 1) ? ~(result >> 1) : (result >> 1);
      pts.push([lat / 1e5, lon / 1e5]);
    }
    return pts;
  }

  // Standard even-odd ray cast. Rings arrive unclosed; the j/i pairing walks the closing edge too.
  function inRing(lat, lon, ring) {
    var inside = false;
    for (var i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      var yi = ring[i][0], xi = ring[i][1], yj = ring[j][0], xj = ring[j][1];
      if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
    }
    return inside;
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (window.L) return resolve();
      var s = document.createElement('script');
      s.src = src; s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  function loadStyle(href) {
    return new Promise(function (resolve) {
      if (document.querySelector('link[href="' + href + '"]')) return resolve();
      var l = document.createElement('link');
      l.rel = 'stylesheet'; l.href = href;
      l.onload = resolve; l.onerror = resolve; // a missing basemap stylesheet must not block the answer
      document.head.appendChild(l);
    });
  }

  class CarniExpressCheck extends HTMLElement {
    connectedCallback() {
      // This sits inside the delivery-zones modal on pages that sell. If anything in here
      // throws, the modal must still open and still show the written zone list underneath.
      try { this.bind(); } catch (e) { console.log('carni-express-check: ' + e); }
    }

    bind() {
      if (this.bound) return;
      this.bound = true;

      this.mapEl = this.querySelector('[data-cec-map]');
      this.resultEl = this.querySelector('[data-cec-result]');
      this.hintEl = this.querySelector('[data-cec-hint]');
      this.locateBtn = this.querySelector('[data-cec-locate]');
      this.pinBtn = this.querySelector('[data-cec-pin]');

      this.locateBtn.addEventListener('click', () => this.locate());
      this.pinBtn.addEventListener('click', () => this.manualPin());
    }

    /* ---------- data ---------- */

    zone() {
      if (!this.zonePromise) {
        this.zonePromise = fetch(this.dataset.zoneUrl)
          .then((r) => r.json())
          .then((z) => {
            z.decoded = z.rings.map(function (r) {
              return { o: decodePolyline(r.o), h: (r.h || []).map(decodePolyline) };
            });
            return z;
          });
      }
      return this.zonePromise;
    }

    test(z, lat, lon) {
      var b = z.bbox;
      var inside = false;
      if (lat >= b[0] && lat <= b[2] && lon >= b[1] && lon <= b[3]) {
        for (var i = 0; i < z.decoded.length; i++) {
          var r = z.decoded[i];
          if (inRing(lat, lon, r.o)) {
            var hole = false;
            for (var h = 0; h < r.h.length; h++) if (inRing(lat, lon, r.h[h])) { hole = true; break; }
            if (!hole) { inside = true; break; }
          }
        }
      }
      return { inside: inside, edge: this.edgeDistance(z, lat, lon) };
    }

    // Metres to the nearest boundary segment. Equirectangular is right to well under a metre
    // over the few hundred metres that actually decide the answer.
    edgeDistance(z, lat, lon) {
      var K = Math.cos((lat * Math.PI) / 180) * 111320, M = 111320, best = Infinity;
      for (var i = 0; i < z.decoded.length; i++) {
        var rings = [z.decoded[i].o].concat(z.decoded[i].h);
        for (var r = 0; r < rings.length; r++) {
          var ring = rings[r];
          for (var a = 0, b = ring.length - 1; a < ring.length; b = a++) {
            var ax = (ring[b][1] - lon) * K, ay = (ring[b][0] - lat) * M;
            var bx = (ring[a][1] - lon) * K, by = (ring[a][0] - lat) * M;
            var dx = bx - ax, dy = by - ay, L = dx * dx + dy * dy;
            var t = L ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / L)) : 0;
            var d = Math.hypot(ax + t * dx, ay + t * dy);
            if (d < best) best = d;
          }
        }
      }
      return best;
    }

    /* ---------- inputs ---------- */

    locate() {
      if (!navigator.geolocation) {
        return this.say('warn', 'This browser cannot share your location',
          'Use "Set my pin on the map" instead and drag the pin to your address.');
      }
      this.busy(this.locateBtn, true);
      this.say('busy', 'Finding you', 'Your browser may ask for permission to share your location.');

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.busy(this.locateBtn, false);
          this.check(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy, true);
        },
        (err) => {
          this.busy(this.locateBtn, false);
          var msg = 'Set your pin on the map instead and drag it to your address.';
          if (err.code === 1) {
            this.say('warn', 'Location permission was declined',
              'No problem. ' + msg + ' Nothing is sent to us either way.');
          } else if (err.code === 3) {
            this.say('warn', 'That took too long', msg);
          } else {
            this.say('warn', 'Your location was not available', msg);
          }
        },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
      );
    }

    manualPin() {
      var origin = (this.dataset.origin || '25.142225,55.22675').split(',');
      this.check(parseFloat(origin[0]), parseFloat(origin[1]), null, false, true);
    }

    /* ---------- result ---------- */

    check(lat, lon, accuracy, fromGps, isSeed) {
      this.busy(this.pinBtn, true);
      this.zone()
        .then((z) => {
          this.busy(this.pinBtn, false);
          this.lastZone = z;
          this.showMap(z, lat, lon);
          if (isSeed) {
            this.say('busy', 'Drag the pin to your address',
              'Move the pin, or tap anywhere on the map, and the answer updates as you go.');
          } else {
            this.report(z, lat, lon, accuracy, fromGps);
          }
        })
        .catch(() => {
          this.busy(this.pinBtn, false);
          this.say('warn', 'The delivery zone could not be loaded',
            'Please try again, or just add your address at checkout and we will confirm Express there.');
        });
    }

    report(z, lat, lon, accuracy, fromGps) {
      var res = this.test(z, lat, lon);
      // Trust the browser's own accuracy figure when it gives one: a 2 km wifi fix must not
      // produce a confident yes just because its centre landed inside.
      var margin = Math.max(EDGE_FLOOR_M, accuracy || 0);
      // "0.02 km" reads like a rounding artefact; under a kilometre people think in metres.
      var dist = res.edge < 1000
        ? Math.round(res.edge / 10) * 10 + ' m'
        : (res.edge / 1000).toFixed(1) + ' km';

      if (res.inside && res.edge >= margin) {
        this.say('yes', 'Yes, you are in the Express zone',
          'You can get 90 minute delivery on Express products, daily 9 AM to 7 PM.');
      } else if (res.inside) {
        this.say('edge', 'You are right on the edge of the zone',
          'Your pin is inside, but only by about ' + dist + ', which is within the margin of error here. Add your address at checkout and we will confirm Express before you pay.');
      } else if (res.edge < 2000) {
        this.say('edge', 'Just outside the Express zone',
          'You are about ' + dist + ' beyond the edge. Move the pin if it is not quite on your address. We still deliver to you on a scheduled slot.');
      } else {
        this.say('no', 'Outside the 90 minute Express zone',
          'You are about ' + dist + ' beyond the edge, so Express does not reach you. We still deliver everywhere in the UAE on a scheduled slot, just pick one at checkout.');
      }

      if (fromGps && accuracy > 1000) {
        this.hintEl.textContent = 'Your browser placed you to within about ' + Math.round(accuracy / 100) / 10 + ' km, which is rough. Drag the pin onto your building for a firm answer.';
        this.hintEl.hidden = false;
      } else {
        this.hintEl.hidden = true;
      }
    }

    say(state, title, body) {
      this.resultEl.hidden = false;
      this.resultEl.dataset.state = state;
      this.resultEl.querySelector('[data-cec-title]').textContent = title;
      this.resultEl.querySelector('[data-cec-body]').textContent = body;
    }

    busy(btn, on) {
      if (!btn) return;
      btn.disabled = !!on;
      btn.toggleAttribute('data-busy', !!on);
    }

    /* ---------- map ---------- */

    showMap(z, lat, lon) {
      var assets = this.dataset;
      return loadStyle(assets.leafletCss)
        .then(() => loadScript(assets.leafletJs))
        .then(() => {
          this.mapEl.hidden = false;
          if (!this.map) this.initMap(z, lat, lon);
          this.setPin(lat, lon);
          // The map is built inside a modal that was display:none until a moment ago, so
          // Leaflet's cached container size can be stale or zero. Re-measure after paint.
          requestAnimationFrame(() => this.map.invalidateSize());
        })
        .catch(() => {
          // No basemap is survivable: the verdict above is already correct without it.
          this.mapEl.hidden = true;
        });
    }

    initMap(z, lat, lon) {
      var L = window.L;
      this.map = L.map(this.mapEl, { attributionControl: true, scrollWheelZoom: false }).setView([lat, lon], 12);
      L.tileLayer(this.dataset.tileUrl, {
        attribution: this.dataset.tileAttribution,
        maxZoom: 19,
      }).addTo(this.map);

      // The delivery contour is deliberately NOT drawn. It is competitively sensitive, and
      // showing it also invites customers to argue with a line on a map rather than read the
      // verdict, which is the thing that is actually authoritative. The map exists only so the
      // customer can see and correct where their pin is; the answer comes from the text.
      this.marker = L.marker([lat, lon], {
        draggable: true,
        keyboard: true,
        icon: L.divIcon({ className: 'cec-pin', html: '<span></span>', iconSize: [26, 26], iconAnchor: [13, 26] }),
      }).addTo(this.map);

      this.marker.on('dragend', () => {
        var p = this.marker.getLatLng();
        this.report(z, p.lat, p.lng, null, false);
      });
      this.map.on('click', (e) => {
        this.marker.setLatLng(e.latlng);
        this.report(z, e.latlng.lat, e.latlng.lng, null, false);
      });
    }

    setPin(lat, lon) {
      if (!this.marker) return;
      this.marker.setLatLng([lat, lon]);
      this.map.setView([lat, lon], Math.max(this.map.getZoom(), 12));
    }
  }

  customElements.define('carni-express-check', CarniExpressCheck);
})();
